import { Box } from '@opendoor/bricks-next';
import type { BoxProps } from '@opendoor/bricks-next';
import Head from 'next/head';

export type PictureSourceSpec = Array<{
  srcSet: string;
  media: string;
  key: string;
}>;

export const Picture: React.FC<{
  preload: boolean;
  lazy: boolean;
  borderRadius?: BoxProps['borderRadius'];
  sources: PictureSourceSpec;
  objectFit?: React.CSSProperties['objectFit'];
  fetchPriority?: 'high' | 'low' | 'auto';
}> = ({ preload, lazy, borderRadius, sources, objectFit = 'cover', fetchPriority }) => {
  const pictureElement = (
    <picture style={{ height: '100%', width: '100%' }}>
      {sources.map(({ srcSet, media, key }) => {
        return <source srcSet={srcSet} media={media} key={`${key}-source`}></source>;
      })}
      {/* Fallback src for browsers that won't process <source> without one */}
      <img
        src="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
        style={{ objectFit, width: '100%', height: '100%' }}
        alt=""
        loading={lazy ? 'lazy' : undefined}
        // @ts-expect-error: fetchpriority is a valid attribute but missing from React 18 types
        fetchpriority={fetchPriority}
      />
    </picture>
  );

  return (
    <>
      {preload && (
        <Head>
          {sources.map(({ srcSet, media, key }) => {
            return (
              <link
                rel="preload"
                as="image"
                href=""
                // @ts-expect-error: react incorrectly capitalizes this if you use
                // the "typescript" suggestion of `imgSrcSet`
                imagesrcset={srcSet}
                media={media}
                key={`${key}-preload`}
                fetchpriority={fetchPriority ?? 'high'}
              />
            );
          })}
        </Head>
      )}
      {borderRadius ? (
        <Box overflow="hidden" borderRadius={borderRadius} height="100%" width="100%">
          {pictureElement}
        </Box>
      ) : (
        pictureElement
      )}
    </>
  );
};

export type PresetPictureSet = Array<{
  minWidth: number;
  maxWidth: number | null;
  // List of preset values here: https://github.com/opendoor-labs/open_listings/blob/dd5e7230ae24d2e79181cb89485efa79aadee705/app/helpers/image_cdn.rb#L6
  preset: string;
  presetScale?: string;
}>;
export type PresetPictureSetOptions = {
  photoUrl: string;
  sizes: PresetPictureSet;
  borderRadius?: BoxProps['borderRadius'];
  debugImageSizes: boolean;
  lazy: boolean;
  dpr?: Array<number> | null;
  preload?: boolean;
  objectFit?: React.CSSProperties['objectFit'];
  fetchPriority?: 'high' | 'low' | 'auto';
  quality?: number;
};

export const generatePresetPictureSetProps = ({
  photoUrl,
  sizes,
  borderRadius,
  lazy,
  dpr = [1, 2, 3],
  preload = false,
  objectFit,
  fetchPriority,
  quality,
  ...props
}: PresetPictureSetOptions) => {
  // sort in reverse because <source> matches in order, required to use
  // the min-width selectors
  const sortedSizes = sizes.sort((a, b) => (a.minWidth <= b.minWidth ? -1 : 1));
  const sources: Array<{ srcSet: string; media: string; key: string }> = sortedSizes.map(
    ({ minWidth, maxWidth, preset, presetScale }) => {
      const paramsRecord: Record<string, string> = {
        preset,
        ...(presetScale ? { presetScale } : {}),
        service: 'cosmos',
        auto: 'format,compress',
        ...(quality !== undefined ? { q: `${quality}` } : {}),
      };
      const srcSet =
        dpr && dpr?.length > 0
          ? dpr
              .map((d) => {
                const finalParams: Record<string, string> = { ...paramsRecord, dpr: `${d}` };
                return `${photoUrl}?${new URLSearchParams(finalParams).toString()} ${d}x`;
              })
              .join(', ')
          : `${photoUrl}?${new URLSearchParams(paramsRecord).toString()}`;
      const media =
        maxWidth === null
          ? `(min-width: ${minWidth}px)`
          : `(min-width: ${minWidth}px) and (max-width: ${maxWidth - 1}px)`;
      return { srcSet, media, key: `${photoUrl}-${minWidth}` };
    },
  );
  return {
    photoUrl,
    preload,
    lazy,
    borderRadius,
    sources,
    objectFit,
    fetchPriority,
    ...props,
  };
};

export const generatePresetPictureSet = (props: PresetPictureSetOptions) => {
  const { photoUrl, preload, lazy, borderRadius, sources, objectFit, fetchPriority } =
    generatePresetPictureSetProps(props);
  return (
    <Picture
      key={photoUrl}
      preload={preload}
      lazy={lazy}
      borderRadius={borderRadius}
      sources={sources}
      objectFit={objectFit}
      fetchPriority={fetchPriority}
    />
  );
};
